import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import Modal from '../Modal.jsx';
import Button from '../Button.jsx';
import AccountIcon from './AccountIcon.jsx';
import InstitutionBadge from '../InstitutionBadge.jsx';
import MaterialIcon from '../../elements/MaterialIcon.jsx';
import InfoTip from '../../elements/InfoTip.jsx';
import AccountLabel from './AccountLabel.jsx';
import AccountSelect, { accountGroup, findOption } from './AccountSelect.jsx';
import InstitutionSelect from './InstitutionSelect.jsx';
import AccountTransactions, { AccountTransactionList, transactionMatchKey, useAccountTransactions } from './AccountTransactions.jsx';
import PlaidBankAccount from '../../../models/PlaidBankAccount.js';
import { ACCOUNT_ICONS, ACCOUNT_TYPES, defaultIconForType, isHexColor, logoForAccount } from '../../../utilities/accountIcons.js';
import { twinHints } from '../../../utilities/accountIdentity.js';
import { accountBalanceOn } from '../../../utilities/accountSections.js';
import { moneyFormatFromCents } from '../../../utilities/moneyFormat.js';
import {
	createAccount,
	createInstitution,
	deleteInstitution,
	fetchMergePreview,
	mergeAccounts,
	mergeInstitutions,
	moveAccount,
	updateAccount,
	updateInstitution,
} from '../../../redux/actions/accounts.js';
import usePlaidAccounts from '../../hooks/usePlaidAccounts.js';
import { holdBackButton } from '../../../utilities/backButtonClose.js';

/** An account's last digits as Account_Service::clean_mask() takes them: 2 to 6 digits, or blank for none. */
const MASK_MIN_DIGITS = 2;
const MASK_MAX_DIGITS = 6;
const MASK_MESSAGE = `Enter ${MASK_MIN_DIGITS} to ${MASK_MAX_DIGITS} digits, or leave it blank.`;

export function isMask(value) {
	return value === '' || new RegExp(`^[0-9]{${MASK_MIN_DIGITS},${MASK_MAX_DIGITS}}$`).test(value);
}

/** What a colour input shows when the colour in force is not one it can hold. */
const FALLBACK_SWATCH = '#808080';

function isLiabilityType(type) {
	return PlaidBankAccount.LIABILITY_TYPES.includes(type);
}

/**
 * The budget's loan categories a debt can be tracked under, by name: what Account_Service's
 * clean_loan_category() takes, which is Category.isLoanCategory() and not deleted. `categories` and
 * `categoryGroups` are the dashboard's Maps, null until it has loaded them.
 */
export function loanCategoryChoices(categories, categoryGroups = null) {
	if(!categories || typeof categories.values !== 'function') {
		return [];
	}
	return [...categories.values()]
		.filter((category) => !category.deleted
			&& typeof category.isLoanCategory === 'function'
			&& category.isLoanCategory(categoryGroups?.get?.(category.category_group_id)))
		.sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

const HEADERS = {
	new: 'Add',
	account: 'Edit account',
	merge_account: 'Merge account',
	institution: 'Edit institution',
	new_account: 'Add account',
	new_institution: 'Add institution',
};

/**
 * Edits an account, or the institution it is grouped under when no account is given. An account's
 * editor can switch to its institution's, which is the only way to reach the institution of a card
 * holding a single account. Either step has a back arrow to the account, and the browser's Back
 * takes it too.
 *
 * `creating` ('account' or 'institution') makes it a form for a new one instead; `institution` then
 * preselects where a new account goes. `creating` 'any' first asks which of the two, and the form
 * chosen has a back arrow to that question.
 *
 * Only what changed is sent. A refusal stays in the editor, in the server's words.
 */
export default function AccountEditModal({ institution = null, account = null, creating = null, onClose, origin = null }) {
	const editor = useAccountEditor({ institution, account, creating });
	return (
		<Modal
			className='account_edit_modal'
			header={editor.header}
			origin={origin}
			onClose={onClose}
			onBack={editor.back}
		>
			{editor.render}
		</Modal>
	);
}

/**
 * The editor's steps, for a modal of the caller's: AccountEditModal's own, or an account summary's,
 * where the editor is a step too. Gives that modal its `header` and back arrow (`back`, or null),
 * and `render(close)` its content. A step in holds the browser's Back, so Back leaves the step
 * before the modal closes.
 *
 * `onLeave`, given, is what the first view goes back to, such as the summary it was opened from: by
 * the arrow, by Back, or by saving. Merging still closes the modal, since the account is gone after.
 * While not `active` it holds nothing.
 */
export function useAccountEditor({ institution = null, account = null, creating = null, onLeave = null, active = true }) {
	const [first] = useState(() => {
		if(creating) {
			return creating === 'any' ? 'new' : 'new_' + creating;
		}
		return account ? 'account' : 'institution';
	});
	const [view, setView] = useState(first);
	const stepped = view !== first;
	const back = () => setView(first);
	const onLeaveRef = useRef(onLeave);
	onLeaveRef.current = onLeave;
	const leaving = active && !!onLeave;
	/** From any step: the next time it is opened, it opens on the first view again. */
	const leave = () => {
		setView(first);
		onLeaveRef.current();
	};

	useEffect(() => {
		if(!leaving) {
			return undefined;
		}
		return holdBackButton(leave);
	}, [leaving]);

	useEffect(() => {
		if(!active || !stepped) {
			return undefined;
		}
		return holdBackButton(back);
	}, [active, stepped]);

	const render = (close) => {
		const done = onLeave ? leave : close;
		switch(view) {
			case 'new':
				return <AddChoice choose={(kind) => setView('new_' + kind)} />;
			case 'new_account':
				return <NewAccountForm institution={institution} close={done} />;
			case 'new_institution':
				return <InstitutionForm institution={null} close={done} />;
			case 'institution':
				return <InstitutionForm institution={institution} close={done} />;
			case 'merge_account':
				return <MergeAccountForm
					institution={institution}
					account={account}
					close={close}
					back={back}
				/>;
			default:
				return <AccountForm
					institution={institution}
					account={account}
					close={done}
					editInstitution={() => setView('institution')}
					mergeAccount={() => setView('merge_account')}
				/>;
		}
	};

	return {
		header: HEADERS[view],
		back: stepped ? back : (onLeave ? leave : null),
		render,
	};
}

/** What the page's Add makes: an account, or an institution to group accounts under. */
function AddChoice({ choose }) {
	return (
		<div className='account_add_choices'>
			<button type='button' className='account_add_choice' data-kind='account' onClick={() => choose('account')}>
				<MaterialIcon>account_balance_wallet</MaterialIcon>
				<span className='account_add_choice_text'>
					<span className='account_add_choice_name'>Account</span>
					<span className='account_add_choice_detail'>A bank account, card or loan to track a balance for</span>
				</span>
			</button>
			<button type='button' className='account_add_choice' data-kind='institution' onClick={() => choose('institution')}>
				<MaterialIcon>account_balance</MaterialIcon>
				<span className='account_add_choice_text'>
					<span className='account_add_choice_name'>Institution</span>
					<span className='account_add_choice_detail'>A bank or lender to group accounts under</span>
				</span>
			</button>
		</div>
	);
}

/**
 * Runs `save` when `hasChanges`, then closes; with nothing to save it just closes. A refusal is shown
 * instead, and the form stays open.
 */
function useSubmit(close) {
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState(null);
	const submit = (hasChanges, save) => {
		if(!hasChanges) {
			close();
			return Promise.resolve();
		}
		setSaving(true);
		setError(null);
		return save()
			.then(() => close())
			.catch((message) => {
				setSaving(false);
				setError(typeof message === 'string' ? message : 'The change could not be saved.');
			});
	};
	return { saving, error, submit };
}

/** Every institution in the budget, with `current` among them even when the list in state lacks it. */
function useInstitutionChoices(current = null) {
	const institutions = usePlaidAccounts() || [];
	if(current && !institutions.some((candidate) => candidate.id === current.id)) {
		return [current, ...institutions];
	}
	return institutions;
}

function institutionLabel(institution) {
	return institution.getName ? institution.getName() : (institution.institution_name || '');
}

function AccountForm({ institution, account, close, editInstitution, mergeAccount }) {
	const dispatch = useDispatch();
	const { isBudgetAdmin } = useSelector((state) => state.plaid.accountOptions || {});
	const categories = useSelector((state) => state.dashboard?.categories ?? null);
	const categoryGroups = useSelector((state) => state.dashboard?.categoryGroups ?? null);
	const institutions = useInstitutionChoices(institution);
	const canMerge = isBudgetAdmin && mergeTargetGroups(institutions, account).length > 0;
	const [institutionId, setInstitutionId] = useState(institution.id);
	const original = {
		display_name: account.display_name ?? '',
		type: account.type,
		icon: account.icon ?? null,
		color: account.color ?? null,
		is_active: !!account.is_active,
		include_in_total: account.user_preferences?.add_to_total_assets ?? true,
		show_transactions: account.show_transactions !== false,
		mask: account.mask ?? '',
		loan_category_id: account.loan_category_id ?? null,
	};
	const [displayName, setDisplayName] = useState(original.display_name);
	const [loanCategoryId, setLoanCategoryId] = useState(original.loan_category_id);
	const [mask, setMask] = useState(original.mask);
	const [maskError, setMaskError] = useState(null);
	const [type, setType] = useState(original.type);
	const [icon, setIcon] = useState(original.icon);
	const [color, setColor] = useState(original.color);
	const [isActive, setIsActive] = useState(original.is_active);
	const [includeInTotal, setIncludeInTotal] = useState(original.include_in_total);
	const [showTransactions, setShowTransactions] = useState(original.show_transactions);
	const { saving, error, submit } = useSubmit(close);

	const groupColor = institution.getColor();
	const shownColor = color || groupColor;
	const bankName = account.official_name ?? account.name ?? '';
	const flipsBalances = type !== original.type && isLiabilityType(type) !== isLiabilityType(original.type);
	/** Negating every balance as the type crosses over is what keeps this the same; the notice says so. */
	const shownBalance = account.getLatestBalance ? accountBalanceOn(account, null) : null;
	const loanChoices = loanCategoryChoices(categories, categoryGroups);
	/** One saved before the dashboard's categories loaded, or since made not a loan, is still offered as it is. */
	const loanChoiceMissing = loanCategoryId !== null && !loanChoices.some((category) => Number(category.id) === loanCategoryId);

	function onSubmit(event) {
		event.preventDefault();
		const changes = {};
		if(displayName.trim() !== original.display_name) {
			changes.display_name = displayName.trim();
		}
		if(type !== original.type) {
			changes.type = type;
		}
		if(icon !== original.icon) {
			changes.icon = icon ?? '';
		}
		if(color !== original.color) {
			changes.color = color ?? '';
		}
		if(isActive !== original.is_active) {
			changes.is_active = isActive;
		}
		if(includeInTotal !== original.include_in_total) {
			changes.include_in_total = includeInTotal;
		}
		if(showTransactions !== original.show_transactions) {
			changes.show_transactions = showTransactions;
		}
		if(mask.trim() !== original.mask) {
			if(!isMask(mask.trim())) {
				setMaskError(MASK_MESSAGE);
				return;
			}
			changes.mask = mask.trim();
		}
		/** Not sent once the type is no longer owed: the server clears it with the type change. */
		if(isLiabilityType(type) && loanCategoryId !== original.loan_category_id) {
			changes.loan_category_id = loanCategoryId === null ? '' : loanCategoryId;
		}
		const changed = Object.keys(changes).length > 0;
		const moving = institutionId !== institution.id;
		submit(changed || moving, () => {
			const saved = changed ? dispatch(updateAccount(account.id, changes)) : Promise.resolve();
			return moving ? saved.then(() => dispatch(moveAccount(account.id, institutionId))) : saved;
		});
	}

	const target = institutions.find((candidate) => candidate.id === institutionId);
	/** What the account is drawn with when it has no icon of its own, in the group it is going to. */
	const defaultLogo = logoForAccount({ icon: null }, target || institution);

	return (
		<form className='account_edit_form' onSubmit={onSubmit}>
			<label className='account_edit_field'>
				<span className='account_edit_label'>Name</span>
				<input
					className='browser-default account_edit_name'
					name='display_name'
					value={displayName}
					maxLength={64}
					placeholder={bankName}
					onChange={(event) => setDisplayName(event.target.value)}
					data-autofocus='fine-pointer'
				/>
				{bankName && <span className='account_edit_hint'>The bank calls it {bankName}. Leave blank to use that.</span>}
			</label>

			<label className='account_edit_field'>
				<span className='account_edit_label'>Last 4 digits</span>
				<input
					className='browser-default account_edit_name account_edit_mask'
					name='mask'
					value={mask}
					maxLength={MASK_MAX_DIGITS}
					inputMode='numeric'
					pattern={`[0-9]{${MASK_MIN_DIGITS},${MASK_MAX_DIGITS}}`}
					title={MASK_MESSAGE}
					aria-invalid={maskError ? true : undefined}
					onChange={(event) => {
						setMask(event.target.value);
						setMaskError(null);
					}}
				/>
				{maskError
					? <span className='account_edit_hint account_edit_mask_error'>{maskError}</span>
					: <span className='account_edit_hint'>Of the account number, to tell it apart from accounts with the same name. Leave blank if unknown.</span>}
			</label>

			<div className='account_edit_field'>
				<span className='account_edit_label'>Institution</span>
				<InstitutionSelect
					institutions={institutions}
					value={institutionId}
					onChange={setInstitutionId}
				/>
				{institutionId !== institution.id && target &&
				<span className='account_edit_hint account_edit_move_hint'>
					Moves it to the end of {institutionLabel(target)}, under that institution’s logo.
				</span>
				}
			</div>

			<div className='account_edit_field'>
				<span className='account_edit_label'>Type</span>
				<TypeChoices type={type} color={shownColor} onChange={setType} />
				{flipsBalances &&
				<span className='account_edit_hint account_edit_flip_warning'>
					Its balances will be turned round so they still read {shownBalance === null ? 'the same' : moneyFormatFromCents(shownBalance)}, {isLiabilityType(type) ? 'now counted as money owed' : 'no longer counted as money owed'}.
				</span>
				}
				{!isLiabilityType(type) && original.loan_category_id !== null &&
				<span className='account_edit_hint account_edit_loan_category_cleared'>
					It will no longer be tracked under a loan category.
				</span>
				}
			</div>

			{isLiabilityType(type) &&
			<label className='account_edit_field'>
				<span className='account_edit_label'>Loan category</span>
				<select
					className='browser-default account_edit_loan_category'
					name='loan_category_id'
					value={loanCategoryId === null ? '' : String(loanCategoryId)}
					onChange={(event) => setLoanCategoryId(event.target.value === '' ? null : Number(event.target.value))}
				>
					<option value=''>None</option>
					{loanChoices.map((category) => (
						<option key={category.id} value={String(category.id)}>{category.name}</option>
					))}
					{loanChoiceMissing &&
					<option value={String(loanCategoryId)}>The category it has now</option>
					}
				</select>
				<span className='account_edit_hint'>The budget category its balance, rate and minimum payment are kept under.</span>
			</label>
			}

			<div className='account_edit_field'>
				<span className='account_edit_label'>Icon</span>
				<div className='account_edit_choices account_edit_icons' role='radiogroup' aria-label='Icon'>
					<ChoiceTile
						selected={icon === null}
						title={defaultLogo ? 'The institution’s logo' : 'The type’s own'}
						caption='Default'
						data={{ 'data-icon': '' }}
						onSelect={() => setIcon(null)}
					>
						<AccountIcon icon={defaultIconForType(type, [displayName, bankName].join(' '))} logo={defaultLogo} color={shownColor} size={28} />
					</ChoiceTile>
					{ACCOUNT_ICONS.map((name) => (
						<ChoiceTile
							key={name}
							selected={icon === name}
							title={name.replace(/_/g, ' ')}
							data={{ 'data-icon': name }}
							onSelect={() => setIcon(name)}
						>
							<AccountIcon icon={name} color={shownColor} size={28} />
						</ChoiceTile>
					))}
				</div>
			</div>

			<ColorField
				color={color}
				fallback={groupColor}
				resetLabel='Use the institution’s colour'
				onChange={setColor}
			/>

			<div className='account_edit_field account_edit_toggles'>
				<AccountToggle
					name='is_active'
					label='Closed'
					tip='Hides the account on the Accounts page unless closed accounts are shown, and leaves it out of the totals there. Its balances and transactions are kept.'
					checked={!isActive}
					onChange={(checked) => setIsActive(!checked)}
				/>
				<AccountToggle
					name='include_in_total'
					label='Count in my totals'
					tip='Adds this account’s balance to the totals on the Accounts page. This is your own setting: other members of the budget choose for themselves.'
					checked={includeInTotal}
					onChange={setIncludeInTotal}
				/>
				<AccountToggle
					name='hide_transactions'
					label='Hide its transactions'
					tip='Keeps its bank transactions off the list waiting to be budgeted and out of the month’s bank activity. Nothing is deleted; turn this off to show them again.'
					checked={!showTransactions}
					onChange={(checked) => setShowTransactions(!checked)}
				/>
			</div>

			<AccountTransactions accountId={account.id} />

			<FormActions
				saving={saving}
				error={error}
				close={close}
				extra={
					<React.Fragment>
						<Button flat type='button' className='account_edit_institution' onClick={editInstitution}>
							Edit {institution.getName() || 'institution'}
						</Button>
						{canMerge &&
						<Button flat type='button' className='account_edit_merge_account' onClick={mergeAccount} disabled={saving}>
							Merge into…
						</Button>
						}
					</React.Fragment>
				}
			/>
		</form>
	);
}

/** One of the account's on/off settings: its name, a help icon explaining it, and a switch. */
function AccountToggle({ name, label, tip, checked, onChange }) {
	const id = `account_edit_${name}`;
	return (
		<div className='account_edit_toggle'>
			<label htmlFor={id} className='account_edit_toggle_label'>{label}</label>
			<InfoTip label={`What ${label} means`}>{tip}</InfoTip>
			<div className='switch account_edit_toggle_switch'>
				<label>
					<input
						id={id}
						type='checkbox'
						name={name}
						checked={checked}
						onChange={(event) => onChange(event.target.checked)}
					/>
					<span className='lever' />
				</label>
			</div>
		</div>
	);
}

/**
 * Edits an institution, or makes one when `institution` is null. Merging it into another and
 * deleting it are offered to budget admins only, and deleting only once it holds no accounts --
 * closed ones count, since deleting would take them with it. Both ask once more before going ahead.
 */
function InstitutionForm({ institution, close }) {
	const dispatch = useDispatch();
	const { logoChoices, isBudgetAdmin } = useSelector((state) => state.plaid.accountOptions || {});
	const institutions = useInstitutionChoices(institution);
	const creating = !institution;
	const original = {
		name: creating ? '' : institution.getName(),
		color: creating ? null : (institution.color ?? null),
		logo: creating ? '' : (institution.institution_id ?? ''),
	};
	const [name, setName] = useState(original.name);
	const [color, setColor] = useState(original.color);
	const [logo, setLogo] = useState(original.logo);
	const [mergeTarget, setMergeTarget] = useState('');
	const [confirming, setConfirming] = useState(null);
	const { saving, error, submit } = useSubmit(close);

	const choices = logoChoices || [];
	const chosenLogo = choices.find((choice) => choice.institution_id === logo)
		|| (!creating && logo === original.logo && logo !== '' ? institution.institution : null);
	/** What the colour falls back to with none chosen: the logo's institution's, or one made from the name. */
	const fallback = chosenLogo?.primary_color || null;
	const others = creating ? [] : institutions.filter((candidate) => candidate.id !== institution.id);
	const accountCount = creating ? 0 : (institution.accounts || []).length;
	const mergeInto = others.find((candidate) => String(candidate.id) === mergeTarget);

	function onSubmit(event) {
		event.preventDefault();
		if(creating) {
			const fields = { name: name.trim() };
			if(color) {
				fields.color = color;
			}
			if(logo) {
				fields.logo_institution_id = logo;
			}
			submit(true, () => dispatch(createInstitution(fields)));
			return;
		}
		const changes = {};
		if(name.trim() !== original.name) {
			changes.name = name.trim();
		}
		if(color !== original.color) {
			changes.color = color ?? '';
		}
		if(logo !== original.logo) {
			changes.logo_institution_id = logo;
		}
		submit(Object.keys(changes).length > 0, () => dispatch(updateInstitution(institution.id, changes)));
	}

	return (
		<form className='account_edit_form' onSubmit={onSubmit}>
			<div className='account_edit_preview'>
				<InstitutionBadge
					institution={chosenLogo}
					name={name}
					color={color || fallback}
					size={40}
				/>
			</div>
			<label className='account_edit_field'>
				<span className='account_edit_label'>Name</span>
				<input
					className='browser-default account_edit_name'
					name='name'
					value={name}
					maxLength={128}
					required
					onChange={(event) => setName(event.target.value)}
					data-autofocus='fine-pointer'
				/>
			</label>

			<div className='account_edit_field'>
				<span className='account_edit_label'>Logo</span>
				<div className='account_edit_choices account_edit_logos' role='radiogroup' aria-label='Logo'>
					<ChoiceTile
						selected={logo === ''}
						title='No logo: initials in its colour'
						caption='None'
						data={{ 'data-logo': '' }}
						onSelect={() => setLogo('')}
					>
						<InstitutionBadge institution={null} name={name} color={color || fallback} size={28} />
					</ChoiceTile>
					{choices.map((choice) => (
						<ChoiceTile
							key={choice.institution_id}
							selected={logo === choice.institution_id}
							title={choice.name || choice.institution_id}
							data={{ 'data-logo': choice.institution_id }}
							onSelect={() => setLogo(choice.institution_id)}
						>
							<InstitutionBadge institution={choice} size={28} />
						</ChoiceTile>
					))}
				</div>
			</div>

			<ColorField
				color={color}
				fallback={fallback || (creating ? FALLBACK_SWATCH : institution.getColor())}
				resetLabel='Use the default colour'
				onChange={setColor}
			/>
			<span className='account_edit_hint'>Its accounts are drawn in this colour unless they have one of their own.</span>

			{!creating && isBudgetAdmin && others.length > 0 &&
			<div className='account_edit_field account_edit_merge'>
				<span className='account_edit_label'>Merge into…</span>
				<InstitutionSelect
					institutions={others}
					value={mergeTarget === '' ? '' : Number(mergeTarget)}
					name='merge_target'
					ariaLabel='Merge into'
					className='account_edit_merge_target'
					onChange={(id) => {
						setMergeTarget(id === '' ? '' : String(id));
						setConfirming(null);
					}}
				/>
				{mergeInto && confirming !== 'merge' &&
				<Button flat small type='button' className='account_edit_merge_button' onClick={() => setConfirming('merge')} disabled={saving}>
					Merge
				</Button>
				}
				{mergeInto && confirming === 'merge' &&
				<div className='account_edit_confirm'>
					<span className='account_edit_hint'>
						Moves {accountCount === 1 ? 'its account' : `all ${accountCount} of its accounts`} into {institutionLabel(mergeInto)} and deletes {original.name || 'this institution'}.
					</span>
					<Button
						small
						type='button'
						className='account_edit_merge_confirm'
						disabled={saving}
						onClick={() => submit(true, () => dispatch(mergeInstitutions(institution.id, mergeInto.id)))}
					>
						Merge into {institutionLabel(mergeInto)}
					</Button>
				</div>
				}
			</div>
			}

			<FormActions
				saving={saving}
				error={error}
				close={close}
				saveLabel={creating ? 'Add' : 'Save'}
				extra={!creating && isBudgetAdmin && accountCount === 0 && (
					confirming === 'delete'
						? <Button
							small
							type='button'
							className='account_edit_delete_confirm'
							danger
							disabled={saving}
							onClick={() => submit(true, () => dispatch(deleteInstitution(institution.id)))}
						>
							Delete {original.name || 'institution'}
						</Button>
						: <Button flat type='button' className='account_edit_delete' onClick={() => setConfirming('delete')} disabled={saving}>
							Delete
						</Button>
				)}
			/>
		</form>
	);
}

function accountLabel(account) {
	const name = (account.getDisplayName ? account.getDisplayName() : (account.display_name ?? account.official_name ?? account.name)) || `Account ${account.id}`;
	const mask = account.mask ? ` ••${account.mask}` : '';
	return name + mask + (account.is_active ? '' : ' (closed)');
}

/** Every other account in the budget, closed ones included, grouped by institution; empty groups left out. */
function mergeTargetGroups(institutions, account) {
	return institutions
		.map((group) => ({
			institution: group,
			accounts: (group.accounts || []).filter((candidate) => candidate.id !== account.id),
		}))
		.filter((group) => group.accounts.length > 0);
}

function plural(count, one, many = one + 's') {
	return `${count} ${count === 1 ? one : many}`;
}

function formatDay(day) {
	return moment(day, 'YYYY-MM-DD').format('MMM D, YYYY');
}

/** "Jan 5, 2026 – Feb 5, 2026", or "none" for an account with no rows of that kind. */
function dateRange({ count, first, last }) {
	if(!count) {
		return 'none';
	}
	return first === last ? formatDay(first) : `${formatDay(first)} – ${formatDay(last)}`;
}

/** How many of each account's newest transactions the merge form lays side by side. */
const MERGE_TRANSACTION_COUNT = 8;

function keysOf(data) {
	return new Set((data?.transactions || []).map(transactionMatchKey));
}

/**
 * Folds this account into another of the budget's. Budget admins only. Choosing the target fetches
 * Account_Merger's preview, which is shown in full before the merge can be confirmed; the source is
 * deleted by the merge, and that cannot be undone. Each account's newest transactions are laid side
 * by side under the preview, with those on both marked, so the choice is made on what they hold.
 */
function MergeAccountForm({ institution, account, close, back }) {
	const dispatch = useDispatch();
	const institutions = useInstitutionChoices(institution);
	const groups = mergeTargetGroups(institutions, account);
	/** Over every account, this one too: its twin is the one most worth telling apart from it. */
	const hints = twinHints(institutions.flatMap((group) => group.accounts || []));
	const options = groups.map(({ institution: group, accounts }) => accountGroup(group, accounts, hints));
	const [targetId, setTargetId] = useState('');
	const [preview, setPreview] = useState(null);
	const [loading, setLoading] = useState(false);
	const [previewError, setPreviewError] = useState(null);
	const asked = useRef(null);
	const { saving, error, submit } = useSubmit(close);

	function choose(value) {
		const id = value === '' ? '' : Number(value);
		setTargetId(id);
		setPreview(null);
		setPreviewError(null);
		asked.current = id;
		if(id === '') {
			setLoading(false);
			return Promise.resolve();
		}
		setLoading(true);
		return dispatch(fetchMergePreview(account.id, id))
			.then((answer) => {
				if(asked.current === id) {
					setPreview(answer);
				}
			})
			.catch((message) => {
				if(asked.current === id) {
					setPreviewError(typeof message === 'string' ? message : 'The merge could not be previewed.');
				}
			})
			.finally(() => {
				if(asked.current === id) {
					setLoading(false);
				}
			});
	}

	const sourceTransactions = useAccountTransactions(preview ? account.id : null, MERGE_TRANSACTION_COUNT);
	const targetTransactions = useAccountTransactions(preview ? targetId : null, MERGE_TRANSACTION_COUNT);
	const sourceKeys = keysOf(sourceTransactions.data);
	const targetKeys = keysOf(targetTransactions.data);
	const anyShared = [...sourceKeys].some((key) => targetKeys.has(key));

	const targetOption = targetId === '' ? null : findOption(options, targetId);
	const sourceName = preview?.source?.name || accountLabel(account);
	const targetName = preview?.target?.name || '';
	const targetOwes = preview ? isLiabilityType(preview.target.type) : false;

	return (
		<div className='account_edit_form account_merge_form'>
			<div className='account_edit_field'>
				<span className='account_edit_label'>Merge {accountLabel(account)} into</span>
				<AccountSelect
					className='account_merge_target'
					name='merge_target_account'
					groups={options}
					value={findOption(options, targetId)}
					onChange={(option) => choose(option ? option.value : '')}
					autoFocus
				/>
				<span className='account_edit_hint'>
					Use this when the same account is here twice. Everything on {accountLabel(account)} moves to the account you choose, and {accountLabel(account)} is deleted.
				</span>
			</div>

			{targetOption &&
			<div className='account_merge_pair'>
				<div className='account_merge_pair_side account_merge_source'>
					<span className='account_edit_label'>Deleted</span>
					<AccountLabel account={account} institution={institution} />
				</div>
				<MaterialIcon className='account_merge_pair_arrow'>arrow_forward</MaterialIcon>
				<div className='account_merge_pair_side account_merge_chosen'>
					<span className='account_edit_label'>Kept</span>
					<AccountLabel identity={targetOption.identity} />
				</div>
			</div>
			}

			{loading && <p className='account_edit_hint account_merge_loading'>Working out what would move…</p>}
			{previewError && <p className='account_edit_error red-text' role='alert'>{previewError}</p>}

			{preview &&
			<div className='account_merge_preview'>
				<table className='account_merge_ranges'>
					<thead>
						<tr><th />
							<th>{sourceName} <span className='account_edit_hint'>(deleted)</span></th>
							<th>{targetName} <span className='account_edit_hint'>(kept)</span></th>
						</tr>
					</thead>
					<tbody>
						<tr className='account_merge_transactions'>
							<th>Transactions</th>
							<td>{preview.source.transactions.count}<br /><span className='account_edit_hint'>{dateRange(preview.source.transactions)}</span></td>
							<td>{preview.target.transactions.count}<br /><span className='account_edit_hint'>{dateRange(preview.target.transactions)}</span></td>
						</tr>
						<tr className='account_merge_balances'>
							<th>Balances</th>
							<td>{preview.source.balances.count}<br /><span className='account_edit_hint'>{dateRange(preview.source.balances)}</span></td>
							<td>{preview.target.balances.count}<br /><span className='account_edit_hint'>{dateRange(preview.target.balances)}</span></td>
						</tr>
					</tbody>
				</table>

				<div className='account_merge_transactions_compare'>
					<div className='account_merge_transactions_side account_merge_transactions_source'>
						<span className='account_edit_label'>Newest on {sourceName}</span>
						<AccountTransactionList
							{...sourceTransactions}
							matches={targetKeys}
							matchTitle={`The same day and amount is on ${targetName}`}
						/>
					</div>
					<div className='account_merge_transactions_side account_merge_transactions_target'>
						<span className='account_edit_label'>Newest on {targetName}</span>
						<AccountTransactionList
							{...targetTransactions}
							matches={sourceKeys}
							matchTitle={`The same day and amount is on ${sourceName}`}
						/>
					</div>
				</div>
				{anyShared &&
				<p className='account_edit_hint account_merge_transactions_legend'>
					Highlighted rows are on both accounts, the same amount on the same day.
				</p>
				}

				<ul className='account_merge_details'>
					<li className='account_merge_moves'>
						Moves {plural(preview.move.transactions, 'transaction')}, {plural(preview.move.balances, 'balance')} and {plural(preview.move.import_maps, 'import mapping')} to {targetName}.
					</li>
					{preview.balance_collisions > 0 &&
					<li className='account_merge_collisions'>
						{plural(preview.balance_collisions, 'balance')} from {sourceName} {preview.balance_collisions === 1 ? 'falls on a day' : 'fall on days'} {targetName} already has a balance for, and will be dropped. {targetName}’s own balance is kept.
					</li>
					}
					{preview.likely_duplicate_transactions > 0 &&
					<li className='account_merge_duplicates'>
						{plural(preview.likely_duplicate_transactions, 'transaction')} {preview.likely_duplicate_transactions === 1 ? 'looks like a duplicate' : 'look like duplicates'}: the same amount on the same day on both accounts. Both copies are kept, so check them after merging.
					</li>
					}
					{preview.resign_balances &&
					<li className='account_merge_resign'>
						{targetOwes
							? `${targetName} is money owed and ${sourceName} was not, so the moved balances will be flipped so they still show as owed.`
							: `${targetName} is money held and ${sourceName} was money owed, so the moved balances will be flipped so they still show the same amounts they did on ${sourceName}.`}
					</li>
					}
					{preview.copy_mask &&
					<li className='account_merge_mask'>
						{targetName} has no account number digits, so it will take ••{preview.source.mask} from {sourceName}.
					</li>
					}
					{preview.user_preferences_removed > 0 &&
					<li className='account_merge_preferences'>
						“Count in my totals” settings on {sourceName} are dropped; {targetName} keeps its own.
					</li>
					}
				</ul>

				<p className='account_merge_warning'>
					{sourceName} will be deleted. This cannot be undone.
				</p>
			</div>
			}

			{error && <p className='account_edit_error red-text' role='alert'>{error}</p>}
			<div className='account_edit_actions'>
				<Button flat type='button' className='account_merge_back' onClick={back} disabled={saving}>Back</Button>
				<span className='account_edit_actions_spacer' />
				<Button flat type='button' onClick={close} disabled={saving}>Cancel</Button>
				<Button
					type='button'
					danger
					className='account_merge_confirm'
					disabled={!preview || saving || loading}
					onClick={() => submit(true, () => dispatch(mergeAccounts(account.id, targetId)))}
				>
					{saving ? 'Merging…' : `Merge and delete ${preview ? sourceName : 'account'}`}
				</Button>
			</div>
		</div>
	);
}

/**
 * A new account: a name, a type, and the institution it goes in. With "Imported accounts" left
 * chosen, it lands where an import would have put it, which the server creates if need be.
 */
function NewAccountForm({ institution, close }) {
	const dispatch = useDispatch();
	const institutions = useInstitutionChoices(institution);
	const [name, setName] = useState('');
	const [type, setType] = useState(ACCOUNT_TYPES[0].value);
	const [mask, setMask] = useState('');
	const [institutionId, setInstitutionId] = useState(institution ? institution.id : '');
	const { saving, error, submit } = useSubmit(close);
	const hasManual = institutions.some((candidate) => candidate.is_manual);

	function onSubmit(event) {
		event.preventDefault();
		const fields = { name: name.trim(), type };
		if(institutionId !== '') {
			fields.institution_id = institutionId;
		}
		if(mask.trim() !== '') {
			fields.mask = mask.trim();
		}
		submit(true, () => dispatch(createAccount(fields)));
	}

	return (
		<form className='account_edit_form' onSubmit={onSubmit}>
			<label className='account_edit_field'>
				<span className='account_edit_label'>Name</span>
				<input
					className='browser-default account_edit_name'
					name='name'
					value={name}
					maxLength={64}
					required
					onChange={(event) => setName(event.target.value)}
					data-autofocus
				/>
			</label>

			<div className='account_edit_field'>
				<span className='account_edit_label'>Type</span>
				<TypeChoices type={type} color={FALLBACK_SWATCH} onChange={setType} />
			</div>

			<div className='account_edit_field'>
				<span className='account_edit_label'>Institution</span>
				<InstitutionSelect
					institutions={institutions.filter((candidate) => !candidate.is_manual)}
					value={institutionId}
					emptyLabel={hasManual ? institutionLabel(institutions.find((candidate) => candidate.is_manual)) : 'Imported accounts'}
					onChange={setInstitutionId}
				/>
			</div>

			<label className='account_edit_field'>
				<span className='account_edit_label'>Last digits</span>
				<input
					className='browser-default account_edit_name account_edit_mask'
					name='mask'
					value={mask}
					maxLength={20}
					inputMode='numeric'
					onChange={(event) => setMask(event.target.value)}
				/>
				<span className='account_edit_hint'>Optional. The last few digits of the account number, to tell it apart.</span>
			</label>

			<FormActions saving={saving} error={error} close={close} saveLabel='Add' />
		</form>
	);
}

/**
 * The account types as a row of tiles, each with its type's icon in `color`. A radiogroup like the
 * Icon picker; there are few enough types that a select only hid them.
 */
function TypeChoices({ type, color, onChange }) {
	return (
		<div className='account_edit_choices account_edit_types' role='radiogroup' aria-label='Type'>
			{ACCOUNT_TYPES.map((option) => (
				<ChoiceTile
					key={option.value}
					selected={type === option.value}
					caption={option.label}
					data={{ 'data-type': option.value }}
					onSelect={() => onChange(option.value)}
				>
					<AccountIcon icon={defaultIconForType(option.value)} color={color} size={28} />
				</ChoiceTile>
			))}
		</div>
	);
}

/**
 * One option of a tile picker: a radio button showing `children`, with `caption` under it when given.
 * `data` is the data attributes that name the option.
 */
function ChoiceTile({ selected, onSelect, title = null, caption = null, data = {}, children }) {
	return (
		<button
			type='button'
			className={'account_edit_choice' + (selected ? ' selected' : '') + (caption ? ' captioned' : '')}
			role='radio'
			aria-checked={selected}
			title={title || undefined}
			onClick={onSelect}
			{...data}
		>
			{children}
			{caption && <span className='account_edit_choice_caption'>{caption}</span>}
			{selected && <MaterialIcon className='account_edit_choice_check'>check_circle</MaterialIcon>}
		</button>
	);
}

/** A colour of its own, or none to fall back to `fallback`. */
function ColorField({ color, fallback, resetLabel, onChange }) {
	const swatch = color || (isHexColor(fallback) ? fallback : FALLBACK_SWATCH);
	return (
		<div className='account_edit_field account_edit_color'>
			<span className='account_edit_label'>Colour</span>
			<input
				type='color'
				name='color'
				className='account_edit_color_input'
				value={swatch.toLowerCase()}
				onChange={(event) => onChange(event.target.value.toLowerCase())}
			/>
			{color
				? <Button flat small type='button' className='account_edit_color_reset' onClick={() => onChange(null)}>{resetLabel}</Button>
				: <span className='account_edit_hint'>Default</span>
			}
		</div>
	);
}

function FormActions({ saving, error, close, extra = null, saveLabel = 'Save' }) {
	return (
		<React.Fragment>
			{error && <p className='account_edit_error red-text' role='alert'>{error}</p>}
			<div className='account_edit_actions'>
				{extra}
				<span className='account_edit_actions_spacer' />
				<Button flat type='button' onClick={close} disabled={saving}>Cancel</Button>
				<Button submit className='account_edit_save' disabled={saving}>{saving ? 'Saving…' : saveLabel}</Button>
			</div>
		</React.Fragment>
	);
}
